<script setup lang="ts">
import type { CityImage, HomeTitleProperties } from '@optionway/models';

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.homeTitle;
const apiUrl = $appConfig.api?.url;

const properties = withDefaults(defineProps<HomeTitleProperties>(), {
  defaultImage: 'default.webp',
});

const pageConfig = computed<HomeTitleProperties>(() => ({
  components: properties.components ?? config?.components,
  hideComponents: properties.hideComponents ?? config?.hideComponents,
}));

const searchCriteria = useSearchCriteriaStore();
const currencyStore = useCurrencyStore();

const { hasError: hasImageError, onError: onImageError } = useImageFallback(() => searchCriteria.destinationCode);

const { data: cityImage } = useQuery(() => useCityImageOptions({ code: searchCriteria.destinationCode ?? '', apiUrl }));

// The city picture, or nothing when it failed: the hero then falls back to its default picture.
const nextPicture = computed(() => (hasImageError.value ? undefined : cityImage.value));
// The shown picture follows the next one until it changes: the one before stays on screen while the next one
// mounts invisible over it, and settle promotes it at the end of its fade. undefined is the default picture.
const previousPicture = usePrevious(nextPicture);
const pending = ref(false);
const incomingLoaded = ref(false);
const hasFaded = ref(false);
watch(nextPicture, () => {
  pending.value = true;
  incomingLoaded.value = false;
});
const shown = computed(() => (pending.value ? previousPicture.value : nextPicture.value));
const incoming = computed<CityImage | 'default' | undefined>(() =>
  pending.value ? (nextPicture.value ?? 'default') : undefined,
);
const settle = () => {
  pending.value = false;
  incomingLoaded.value = false;
  hasFaded.value = true;
};
</script>

<template>
  <div class="relative flex min-h-96 p-4 md:p-6 lg:min-h-104 lg:p-8">
    <!-- The picture on screen stays until the next one, mounted invisible over it, has loaded and faded in.
         breakpoint 450: the narrow picture is a 450px-wide source, so it only serves phone widths -->
    <UiCustomResponsivePicture
      v-if="shown"
      :key="shown.wideUrl"
      :src="shown.wideUrl"
      :narrow-src="shown.narrowUrl"
      :breakpoint="450"
      :placeholder="shown.widePlaceholder"
      :narrow-placeholder="shown.narrowPlaceholder"
      :no-placeholder="hasFaded"
      sizes="100vw"
      fit="cover"
      legacy-format="webp"
      alt=""
      class="absolute inset-0"
      :img-attrs="{ class: 'h-full w-full object-cover object-center' }"
      @error="onImageError"
    />
    <UiCustomResponsivePicture
      v-else
      key="default"
      :src="`home/hero/${properties.defaultImage}`"
      :no-placeholder="hasFaded"
      sizes="100vw"
      fit="cover"
      legacy-format="webp"
      alt=""
      class="absolute inset-0"
      :img-attrs="{ class: 'h-full w-full object-cover object-center' }"
    />
    <UiCustomResponsivePicture
      v-if="incoming === 'default'"
      key="incoming-default"
      :src="`home/hero/${properties.defaultImage}`"
      no-placeholder
      sizes="100vw"
      fit="cover"
      legacy-format="webp"
      alt=""
      :class="[
        'absolute inset-0 transition-opacity duration-700 ease-in-out',
        incomingLoaded ? 'opacity-100' : 'opacity-0',
      ]"
      :img-attrs="{ class: 'h-full w-full object-cover object-center' }"
      @load="incomingLoaded = true"
      @transitionend="settle"
    />
    <UiCustomResponsivePicture
      v-else-if="incoming"
      :key="`incoming-${incoming.wideUrl}`"
      :src="incoming.wideUrl"
      :narrow-src="incoming.narrowUrl"
      :breakpoint="450"
      no-placeholder
      sizes="100vw"
      fit="cover"
      legacy-format="webp"
      alt=""
      :class="[
        'absolute inset-0 transition-opacity duration-700 ease-in-out',
        incomingLoaded ? 'opacity-100' : 'opacity-0',
      ]"
      :img-attrs="{ class: 'h-full w-full object-cover object-center' }"
      @load="incomingLoaded = true"
      @transitionend="settle"
      @error="onImageError"
    />
    <div class="pointer-events-none absolute inset-0 bg-gradient-to-b from-scrim-25 via-scrim-25 to-scrim-10" />
    <div class="relative w-full">
      <UiCustomContainer>
        <div class="flex flex-col items-center gap-8 pt-4 lg:pt-8">
          <div class="flex flex-col items-center gap-3">
            <h1
              class="text-pretty text-center text-4xl font-bold leading-tight text-text-primary-inverse drop-shadow-hero md:text-5xl"
            >
              {{ $t('app.title') }}
            </h1>
            <h2 class="text-xl font-medium text-text-primary-inverse opacity-90 md:text-2xl lg:text-3xl">
              {{ $t('app.subtitle') }}
            </h2>
          </div>
          <div
            v-if="
              !pageConfig.hideComponents?.homeEkomiNotation ||
              (!pageConfig.hideComponents?.homeAlmaPill && currencyStore.isEuro)
            "
            class="inline-flex items-center gap-3 rounded-full bg-scrim-20 px-4 py-1.5 backdrop-blur-md"
          >
            <HomeEkomiNotation v-if="!pageConfig.hideComponents?.homeEkomiNotation" />
            <div
              v-if="
                !pageConfig.hideComponents?.homeEkomiNotation &&
                !pageConfig.hideComponents?.homeAlmaPill &&
                currencyStore.isEuro
              "
              class="h-4 w-px bg-surface-veil-30"
              aria-hidden="true"
            />
            <HomeAlmaPill v-if="!pageConfig.hideComponents?.homeAlmaPill && currencyStore.isEuro" />
          </div>
        </div>
      </UiCustomContainer>
    </div>
  </div>
</template>
